import React, { useEffect, useState, useRef } from 'react';
import { Avatar, CircularProgress } from '@mui/material';
import iconService from '../services/iconService';

const TokenIcon = ({ coinSymbol, size = 32 }) => {
  const [iconUrl, setIconUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));
  
  // Listen for icon service updates
  useEffect(() => {
    const handleIconUpdate = () => {
      setUpdateTrigger(prev => prev + 1);
    };
    
    iconService.addListener(handleIconUpdate);
    
    return () => {
      iconService.removeListener(handleIconUpdate);
    };
  }, []);
  
  useEffect(() => {
    const loadIcon = async () => {
      if (!coinSymbol) return;
      
      // Register this instance
      iconService.registerInstance(coinSymbol, instanceId.current);
      
      // Reset state
      setIsLoading(false);
      setIsLoaded(false);
      setIsFailed(false);
      
      // Get coin data to access all available URLs
      const coin = iconService.getCoinData(coinSymbol);
      if (!coin) {
        // console.log(`[TokenIcon:${instanceId.current}] No coin data found for ${coinSymbol}`);
        setIsFailed(true);
        iconService.unregisterInstance(coinSymbol, instanceId.current);
        return;
      }
      
      // Get all available URLs for this coin
      const urls = iconService.getAvailableIconUrls(coinSymbol);
      
      if (urls.length === 0) {
        // console.log(`[TokenIcon:${instanceId.current}] No URLs available for ${coinSymbol}`);
        setIsFailed(true);
        iconService.unregisterInstance(coinSymbol, instanceId.current);
        return;
      }
      
      // console.log(`[TokenIcon:${instanceId.current}] Loading icon for ${coinSymbol} with ${urls.length} URLs:`, urls);
      
      // Debug: Show URL status for this coin
      const urlStatus = iconService.getCoinUrlStatus(coinSymbol);
      // console.log(`[TokenIcon:${instanceId.current}] URL status for ${coinSymbol}:`, urlStatus);
      
      // Check if another instance is already processing this coin
      if (iconService.isCoinBeingProcessed(coinSymbol)) {
        // console.log(`[TokenIcon:${instanceId.current}] ⏳ Another instance is processing ${coinSymbol}, waiting...`);
        setIsLoading(true);
        
        // Wait for processing to complete
        const checkProcessing = () => {
          if (!iconService.isCoinBeingProcessed(coinSymbol)) {
            // Check if any URL is now loaded
            const loadedUrl = urls.find(url => iconService.isIconLoaded(url));
            if (loadedUrl) {
              // console.log(`[TokenIcon:${instanceId.current}] ✅ Found loaded URL for ${coinSymbol}: ${loadedUrl}`);
              setIconUrl(loadedUrl);
              setIsLoading(false);
              setIsLoaded(true);
            } else {
              // Continue with our own loading process
              loadIconUrls();
            }
          } else {
            setTimeout(checkProcessing, 100);
          }
        };
        checkProcessing();
        return;
      }
      
      // Mark as processing
      iconService.setCoinProcessing(coinSymbol, true);
      
      // Load the URLs
      await loadIconUrls();
      
      // Mark as not processing
      iconService.setCoinProcessing(coinSymbol, false);
      
      async function loadIconUrls() {
        // Try each URL in order until one works
        let foundWorkingUrl = false;
        for (let i = 0; i < urls.length && !foundWorkingUrl; i++) {
          const url = urls[i];
          // console.log(`[TokenIcon:${instanceId.current}] Trying URL ${i + 1}/${urls.length} for ${coinSymbol}: ${url}`);
          
          // Check if already loaded
          if (iconService.isIconLoaded(url)) {
            // console.log(`[TokenIcon:${instanceId.current}] ✅ URL already loaded for ${coinSymbol}: ${url}`);
            setIconUrl(url);
            setIsLoaded(true);
            foundWorkingUrl = true;
            break;
          }
          
          // Check if currently loading
          if (iconService.isIconLoading(url)) {
            // console.log(`[TokenIcon:${instanceId.current}] ⏳ URL currently loading for ${coinSymbol}: ${url}`);
            setIconUrl(url);
            setIsLoading(true);
            
            // Wait for loading to complete
            const checkLoading = () => {
              if (iconService.isIconLoaded(url)) {
                // console.log(`[TokenIcon:${instanceId.current}] ✅ URL finished loading for ${coinSymbol}: ${url}`);
                setIsLoading(false);
                setIsLoaded(true);
              } else if (iconService.isIconFailed(url)) {
                // console.log(`[TokenIcon:${instanceId.current}] ❌ URL failed loading for ${coinSymbol}: ${url}`);
                setIsLoading(false);
                // Continue to next URL by breaking out of the loop
                foundWorkingUrl = false; // This will cause the loop to continue
                return;
              } else {
                setTimeout(checkLoading, 100);
              }
            };
            checkLoading();
            return;
          }
          
          // Check if failed - skip this URL
          if (iconService.isIconFailed(url)) {
            // console.log(`[TokenIcon:${instanceId.current}] ❌ URL already failed for ${coinSymbol}: ${url}`);
            continue;
          }
          
          // Try to load this URL
          // console.log(`[TokenIcon:${instanceId.current}] 🔄 Attempting to load URL for ${coinSymbol}: ${url}`);
          setIconUrl(url);
          setIsLoading(true);
          
          const success = await iconService.loadIcon(url);
          
          if (success) {
            // console.log(`[TokenIcon:${instanceId.current}] ✅ Successfully loaded URL for ${coinSymbol}: ${url}`);
            setIsLoading(false);
            setIsLoaded(true);
            foundWorkingUrl = true;
            break;
          } else {
            // console.log(`[TokenIcon:${instanceId.current}] ❌ Failed to load URL for ${coinSymbol}: ${url}`);
            setIsLoading(false);
            // Continue to next URL
          }
        }
        
        // If we get here, all URLs failed
        if (!foundWorkingUrl) {
          // console.log(`[TokenIcon:${instanceId.current}] 💥 All URLs failed for ${coinSymbol}`);
          setIsFailed(true);
        }
      }
    };
    
    loadIcon();
    
    // Cleanup function
    return () => {
      if (coinSymbol) {
        iconService.unregisterInstance(coinSymbol, instanceId.current);
      }
    };
  }, [coinSymbol, updateTrigger]);

  if (iconUrl && isLoaded) {
    return (
      <Avatar 
        src={iconUrl} 
        alt={coinSymbol} 
        sx={{ 
          width: size, 
          height: size
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <Avatar sx={{ 
        width: size, 
        height: size, 
        bgcolor: 'grey.300',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress size={size * 0.5} />
      </Avatar>
    );
  }
  
  // Fallback to coin symbol
  return (
    <Avatar sx={{ 
      width: size, 
      height: size, 
      bgcolor: 'primary.main',
      fontSize: `${size * 0.4}px` // Scale font size with avatar size
    }}>
      {(coinSymbol || '??').substring(0, 2).toUpperCase()}
    </Avatar>
  );
};

export default TokenIcon;